import axios from "axios";
import React, { Component, Fragment } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import ApplicationLoader from "../../components/ui/loader/ApplicationLoader";
import { PasswordRequirements } from "../../components/ui/passwordRequirements/PasswordRequirements";
import { isValidPasswordForEnv } from "../../helpers/FormValidation";
import { getAxiosConfig } from "../../helpers/GeneralHelper";
import { getRecaptchaSiteKey } from "../../helpers/InitAppHelper";
import URLHelper from "../../helpers/URLHelper";
import { trackError } from "@wevideo/core/errorTracking";
import FooterMinView from "../sign-in/signInComponents/FooterMinView";
import LoginToolbarView from "../sign-in/signInComponents/LoginToolbarView";
import "./NewPassword.scss";

const PASSWORD_COMPLEXITY_REASONS = [
	"PasswordTooShort",
	"PasswordMissingUppercase",
	"PasswordMissingLowercase",
	"PasswordMissingDigit",
	"PasswordMissingSpecial",
];

class NewPassword extends Component<any, any> {
	[key: string]: any;
	static defaultProps: any;
	static propTypes: any;
	constructor(props) {
		super(props);

		this.recaptchaRef = React.createRef();

		this.state = {
			showLoader: false,
			pageTitle: "",
			email: "",
			password: "",
			passwordTouched: false,
			confirmPassword: "",
			confirmTouched: false,
			vc: "false",
			newPassInfo: "",
			campaignUser: true,
			error: "",
			submitButtonText: "Change my password",
			recaptchaResponse: "",
		};
	}

	componentDidMount() {
		document.title = "Reset Password - WeVideo";
		const vc = URLHelper.getUrlParamValue(this.props, "vc");
		if (vc !== null) {
			this.setState({ vc: vc });
		}

		let email = URLHelper.getUrlParamValue(this.props, "email");
		if (email !== null) {
			let campaignUser = false;
			if (window.location.hash !== "" && window.location.hash.indexOf("campaign") !== -1) {
				/* Ugh, could be a campaign email and they contain hashes */
				email += window.location.hash;
				campaignUser = true;
			}
			this.setState({ email: email, campaignUser: campaignUser });
		}
	}

	submitRecaptchaResetPassForm = () => {
		this.recaptchaRef.current?.execute();
	};

	handleFormSubmit = event => {
		event.preventDefault();
		return false;
	};

	handleShowLoader = show => {
		this.setState({ showLoader: show });
	};

	handleSubmit = () => {
		const axiosConfig: Record<string, any> = getAxiosConfig();
		axiosConfig.headers["Cache-Control"] = "no-cache";
		axiosConfig.headers["Pragma"] = "no-cache";
		axiosConfig.headers["Expires"] = "0";
		axiosConfig.cache = false;

		const newPassData = {
			email: this.state.email,
			newPassword: this.state.password,
			confirmNewPassword: this.state.confirmPassword,
			vc: this.state.vc,
			recaptchaResponse: this.state.recaptchaResponse,
		};
		axios
			.post("/api/3/users/changepassword", newPassData, axiosConfig)
			.then(response => {
				if (response.hasOwnProperty("data")) {
					const resp = response.data;
					if (resp.success) {
						if (this.campaignUser) {
							this.setState({ newPassInfo: "Success! Your password has been changed" });
						} else {
							let count = 5;
							const countdown = setInterval(() => {
								this.setState({
									newPassInfo:
										"Success!\nYou will be redirected to login page in " + count + " seconds",
								});
								if (count === 0) {
									clearInterval(countdown);
									window.location.href = "//" + window.location.hostname + "/sign-in";
								}
								count--;
							}, 1000);
						}
					} else {
						this.setState({ submitButtonText: "RESET" });
						if (resp.hasOwnProperty("failedValidations") && resp.failedValidations.length > 0) {
							for (let i = 0; i < resp.failedValidations.length; i++) {
								if (resp.failedValidations[i] === "emptyPasswords") {
									this.setState({
										error: "Password cannot be empty.",
									});
								} else if (PASSWORD_COMPLEXITY_REASONS.includes(resp.failedValidations[i])) {
									this.setState({ error: "Password does not meet the requirements." });
								} else if (resp.failedValidations[i] === "PasswordsNotMatch") {
									this.setState({ confirmTouched: true, error: "" });
								} else {
									this.setState({
										error:
											"There was a problem sending your new password. Please try again. If the problem persists, please contact WeVideo support.",
									});
								}
							}
						} else {
							//Password validation passes but user has already resetted his password once
							this.setState({ error: "This link has expired." });
						}
					}
				}
				this.handleShowLoader(false);
			})
			.catch(error => {
				trackError(error);
				trackError("RESET PASSWORD ERROR");
				//Connection problem, server error etc.
				this.setState({
					submitButtonText: "RESET",
					error:
						"There was a problem sending your new password. Please try again. If the problem persists, please contact WeVideo support.",
				});
				this.handleShowLoader(false);
			});
	};

	recaptchaResolved = (token: string | null = null) => {
		if (token === null) {
			return;
		}
		this.handleShowLoader(true);
		this.recaptchaRef.current?.reset();
		this.setState({ recaptchaResponse: token }, () => this.handleSubmit());
	};

	submitRecaptchaNewPass = () => {
		this.recaptchaRef.current?.execute();
	};

	handlePassChange = event => {
		this.setState({ password: event.target.value.replace(/\s/g, ""), error: "" });
	};

	handlePassBlur = () => {
		this.setState({ passwordTouched: true });
	};

	handleConfirmPassChange = event => {
		this.setState({ confirmPassword: event.target.value.replace(/\s/g, ""), error: "" });
	};

	handleConfirmPassBlur = () => {
		this.setState({ confirmTouched: true });
	};

	render() {
		return (
			<Fragment>
				<ApplicationLoader showLoader={this.state.showLoader} />
				<div className="new-password-page-wrapper">
					<LoginToolbarView />
					<div className="new-password-card">
						<div className="new-password-inner">
							<div className="card-title">Reset password</div>
							<div className="card-text">
								<div className="card-subtitle">Create a new password for your account</div>
								<div className="link">
									<a href="/sign-in">
										{this.state.email} - <span>Not you?</span>
									</a>
								</div>

								<form
									id="resetPasswordForm"
									action=""
									method="post"
									autoComplete="off"
									noValidate
									onSubmit={this.handleFormSubmit}
								>
									<ReCAPTCHA
										ref={this.recaptchaRef}
										sitekey={getRecaptchaSiteKey()}
										size="invisible"
										onChange={this.recaptchaResolved}
										onExpired={() => {}}
									/>
									<div className="inputRow">
										<input
											id="password"
											type="password"
											name="newPassword"
											placeholder="Enter new password"
											required
											autoComplete="off"
											maxLength={50}
											value={this.state.password}
											className={
												this.state.passwordTouched && !isValidPasswordForEnv(this.state.password)
													? "input-error"
													: undefined
											}
											onChange={event => {
												this.handlePassChange(event);
											}}
											onBlur={this.handlePassBlur}
										/>
										<PasswordRequirements password={this.state.password} />
										{this.state.passwordTouched && this.state.password.trim() === "" && (
											<div className="message error">Password is required</div>
										)}
									</div>
									<div className="inputRow">
										<input
											id="confirm_password"
											type="password"
											name="confirmNewPassword"
											placeholder="Confirm password"
											required
											autoComplete="off"
											maxLength={50}
											value={this.state.confirmPassword}
											onChange={event => {
												this.handleConfirmPassChange(event);
											}}
											onBlur={this.handleConfirmPassBlur}
										/>
										{this.state.confirmTouched && this.state.confirmPassword.trim() === "" && (
											<div className="message error">Please confirm your password</div>
										)}
										{this.state.confirmTouched && this.state.confirmPassword !== "" && (
											<div
												className={
													"message " +
													(this.state.password !== this.state.confirmPassword ? "error" : "success")
												}
											>
												{this.state.password !== this.state.confirmPassword
													? "Passwords do not match"
													: "✓ Passwords match"}
											</div>
										)}
									</div>

									{this.state.newPassInfo !== "" && (
										<div className="message">{this.state.newPassInfo}</div>
									)}

									<div className="submitRow">
										<input
											type="submit"
											value={this.state.submitButtonText}
											name="Reset"
											className="submitBtn"
											id="submit"
											data-trackingid="Reset-Password-Reset"
											disabled={
												!isValidPasswordForEnv(this.state.password) ||
												this.state.password !== this.state.confirmPassword ||
												this.state.confirmPassword === ""
											}
											hidden={this.state.newPassInfo !== ""}
											onClick={this.submitRecaptchaNewPass}
										/>
									</div>
									<div id="feedback" className="feedback error" hidden={this.state.error === ""}>
										{this.state.error}
									</div>
								</form>
							</div>
						</div>
					</div>
					<FooterMinView />
				</div>
			</Fragment>
		);
	}
}

export default NewPassword;
